import { useEffect, useRef, useMemo } from "react";
import {
  useAuthActions,
  useIsAdmin,
  useIsGroupNurse,
  useLoginUser,
} from "./useAuth";
import { useGroupStore } from "@store/groupStore";
import { useAdminGroupsQuery } from "./useAdminGroupsQuery";
import { useGroupNurseGroupsQuery } from "./useGroupNurseGroupsQuery";
import httpClient from "@/config/axios";

export function useGroupSelection() {
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  const isGroupNurse = useIsGroupNurse();
  const { setLoginUser } = useAuthActions();
  const {
    selectedGroupId,
    setSelectedGroupId,
    selectedOfficeId,
    setSelectedOfficeId,
  } = useGroupStore();

  const {
    data: fetchedGroups = [],
    isLoading: isGroupsLoading,
    refetch: fetchGroups,
  } = useAdminGroupsQuery();
  const { data: groupNurseGroups = [], isLoading: isGroupNurseGroupsLoading } =
    useGroupNurseGroupsQuery(isGroupNurse);
  const isGroupSelectionLoading = isAdmin
    ? isGroupsLoading
    : isGroupNurse
      ? isGroupNurseGroupsLoading
      : false;

  // 관리자가 아닐 경우 자신의 그룹 정보를 담은 배열을 반환 (useMemo로 메모이제이션)
  const groups = useMemo(
    () =>
      isAdmin
        ? fetchedGroups
        : loginUser?.group_id
          ? [
              {
                group_id: loginUser.group_id,
                office_id: loginUser.office_id,
                group_name: loginUser.mb_part_name,
              },
            ]
          : [],
    [
      isAdmin,
      fetchedGroups,
      loginUser?.group_id,
      loginUser?.office_id,
      loginUser?.mb_part_name,
    ]
  );

  // 이전 값 추적을 위한 ref (무한 루프 방지)
  const prevSelectedGroupIdRef = useRef<string | null>(selectedGroupId);
  const prevSelectedOfficeIdRef = useRef<string | null>(selectedOfficeId);
  const prevGroupsLengthRef = useRef<number>(groups.length);
  const initializedRef = useRef<boolean>(false);
  const switchingGroupIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loginUser) return; // User not logged in, wait or redirect

    if (isAdmin) {
      // If groups are loaded and selectedGroupId is not yet determined (or invalid)
      if (!isGroupsLoading && groups.length > 0) {
        // Find the group based on selectedGroupId from store/local storage
        const currentGroupInStore = groups.find(
          (g: { group_id: string }) => g.group_id === selectedGroupId
        );

        if (!selectedGroupId || !currentGroupInStore) {
          // If store has no value, or saved value is invalid/not in current list
          // Prefer user's home group first when available, otherwise fallback to first group.
          const homeGroup =
            loginUser?.group_id != null
              ? groups.find(
                  (g: { group_id: string }) => g.group_id === loginUser.group_id
                )
              : undefined;
          const fallbackGroup = homeGroup ?? groups[0];
          const newGroupId = fallbackGroup?.group_id || null;
          const newOfficeId = fallbackGroup?.office_id || null;

          // 실제로 변경이 필요한 경우에만 업데이트
          if (prevSelectedGroupIdRef.current !== newGroupId) {
            setSelectedGroupId(newGroupId);
            prevSelectedGroupIdRef.current = newGroupId;
          }
          if (prevSelectedOfficeIdRef.current !== newOfficeId) {
            setSelectedOfficeId(newOfficeId);
            prevSelectedOfficeIdRef.current = newOfficeId;
          }
        } else {
          // If store already has a valid selectedGroupId
          const newOfficeId = currentGroupInStore.office_id || null;
          if (prevSelectedOfficeIdRef.current !== newOfficeId) {
            setSelectedOfficeId(newOfficeId);
            prevSelectedOfficeIdRef.current = newOfficeId;
          }
        }
      } else if (!isGroupsLoading && groups.length === 0) {
        // No groups available for admin
        if (prevSelectedGroupIdRef.current !== null) {
          setSelectedGroupId(null);
          prevSelectedGroupIdRef.current = null;
        }
        if (prevSelectedOfficeIdRef.current !== null) {
          setSelectedOfficeId(null);
          prevSelectedOfficeIdRef.current = null;
        }
      }
    } else if (isGroupNurse) {
      if (isGroupNurseGroupsLoading) return;

      // 그룹관리자(HN): 현재 토큰 group_id가 더 이상 유효하지 않으면 switch-group으로 토큰 갱신
      if (groupNurseGroups.length > 0) {
        const currentGroupValid = groupNurseGroups.some(
          (g: { group_id: string }) => g.group_id === loginUser.group_id
        );

        if (!currentGroupValid) {
          const homeGroup =
            loginUser.original_group_id != null
              ? groupNurseGroups.find(
                  (g: { group_id: string }) =>
                    g.group_id === loginUser.original_group_id
                )
              : undefined;
          const fallbackGroup = homeGroup ?? groupNurseGroups[0];
          const fallbackGroupId = fallbackGroup?.group_id ?? null;
          const fallbackOfficeId = fallbackGroup?.office_id ?? null;

          if (
            fallbackGroupId &&
            switchingGroupIdRef.current !== fallbackGroupId
          ) {
            switchingGroupIdRef.current = fallbackGroupId;
            void httpClient
              .post<{ group_id: string; group_name: string }>(
                "/auth/switch-group",
                {
                  target_group_id: fallbackGroupId,
                }
              )
              .then((data) => {
                if (loginUser) {
                  setLoginUser({
                    ...loginUser,
                    group_id: data.group_id,
                    mb_part_name: data.group_name,
                  });
                }
                setSelectedGroupId(data.group_id);
                setSelectedOfficeId(fallbackOfficeId);
                prevSelectedGroupIdRef.current = data.group_id;
                prevSelectedOfficeIdRef.current = fallbackOfficeId;
              })
              .finally(() => {
                switchingGroupIdRef.current = null;
              });
          }
        } else {
          // 유효하면 store와 동기화
          const expectedGroupId = loginUser.group_id || null;
          const matchedGroup = groupNurseGroups.find(
            (g: { group_id: string }) => g.group_id === expectedGroupId
          );
          const expectedOfficeId =
            matchedGroup?.office_id ?? loginUser.office_id;

          if (prevSelectedGroupIdRef.current !== expectedGroupId) {
            setSelectedGroupId(expectedGroupId);
            prevSelectedGroupIdRef.current = expectedGroupId;
          }
          if (prevSelectedOfficeIdRef.current !== expectedOfficeId) {
            setSelectedOfficeId(expectedOfficeId);
            prevSelectedOfficeIdRef.current = expectedOfficeId;
          }
        }
      }
    } else {
      // Non-admin: use user's group/office
      const expectedGroupId = loginUser.group_id || null;
      const expectedOfficeId = loginUser.office_id || null;

      // 실제로 변경이 필요한 경우에만 업데이트
      if (prevSelectedGroupIdRef.current !== expectedGroupId) {
        setSelectedGroupId(expectedGroupId);
        prevSelectedGroupIdRef.current = expectedGroupId;
      }
      if (prevSelectedOfficeIdRef.current !== expectedOfficeId) {
        setSelectedOfficeId(expectedOfficeId);
        prevSelectedOfficeIdRef.current = expectedOfficeId;
      }
    }

    // groups 길이 변경 추적
    prevGroupsLengthRef.current = groups.length;
    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loginUser,
    isAdmin,
    isGroupNurse,
    groups,
    groupNurseGroups,
    isGroupsLoading,
    isGroupNurseGroupsLoading,
    // selectedGroupId와 selectedOfficeId를 dependency에서 제거하여 무한 루프 방지
    // 대신 useRef로 이전 값을 추적하여 실제 변경이 필요한 경우에만 업데이트
    setSelectedGroupId,
    setSelectedOfficeId,
    setLoginUser,
  ]);

  return {
    groups,
    fetchGroups,
    selectedGroupId,
    setSelectedGroupId,
    selectedOfficeId,
    setSelectedOfficeId,
    isAdmin,
    isGroupsLoading,
    isGroupNurseGroupsLoading,
    isGroupSelectionLoading,
  };
}
